"use client"

import React, { useEffect, useState } from "react"
import axios from "axios"
import { Briefcase, Settings, Activity, TrendingUp, Edit, Trash2, Plus } from "lucide-react"

const API_ROUTES = [
  { title: "Stages", path: "stage", icon: <TrendingUp className="w-5 h-5" /> },
  { title: "Sectors", path: "sector", icon: <Activity className="w-5 h-5" /> },
  { title: "Startup Stages", path: "startupstage", icon: <Settings className="w-5 h-5" /> },
  { title: "Industries", path: "industry", icon: <Briefcase className="w-5 h-5" /> },
]

export default function StageSectorIndustryUI() {
  const [activeTab, setActiveTab] = useState("stage")
  const [data, setData] = useState([])
  const [input, setInput] = useState("")
  const [editingId, setEditingId] = useState(null)
  const [showConfirm, setShowConfirm] = useState(false)
  const [deleteId, setDeleteId] = useState(null)

  useEffect(() => {
    fetchData()
  }, [activeTab])

  const fetchData = async () => {
    try {
      const res = await axios.get(`${process.env.REACT_APP_API_URL}/api/${activeTab}`)
      setData(res.data)
    } catch (err) {
      console.error("Error fetching data:", err)
    }
  }

  const handleSubmit = async (e) => {
  e.preventDefault();
  try {
    let fieldName;
    switch(activeTab) {
      case "sector":
        fieldName = "sectorName";
        break;
      case "industry":
        fieldName = "industryName";
        break;
      default:
        fieldName = "stageName"; 
    }

    const payload = { [fieldName]: input };

    if (editingId) {
      await axios.put(`${process.env.REACT_APP_API_URL}/api/${activeTab}/${editingId}`, payload);
    } else {
      await axios.post(`${process.env.REACT_APP_API_URL}/api/${activeTab}`, payload);
    }
    
    setInput("");
    setEditingId(null);
    fetchData();
  } catch (err) {
    alert("Error submitting data");
    console.error(err);
  }
};

  const handleEdit = (item) => {
    setInput(item.stageName)
    setEditingId(item._id)
  }

  const handleDeleteClick = (id) => {
    setDeleteId(id)
    setShowConfirm(true)
  }

  const confirmDelete = async () => {
    try {
      await axios.delete(`${process.env.REACT_APP_API_URL}/api/${activeTab}/${deleteId}`)
      fetchData()
      setDeleteId(null)
      setShowConfirm(false)
    } catch (err) {
      console.error("Error deleting data:", err)
    }
  }

  const activeRoute = API_ROUTES.find((r) => r.path === activeTab)

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="w-full mx-auto p-6">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-4 mb-6">
            <div className="w-12 h-12 bg-blue-600 rounded-xl flex items-center justify-center">
              <Plus className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Add Data</h1>
              <p className="text-gray-500">Manage stages, sectors, and industries</p>
            </div>
          </div>

          {/* Tab Navigation */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {API_ROUTES.map((route) => (
              <button
                key={route.path}
                onClick={() => {
                  setActiveTab(route.path)
                  setInput("")
                  setEditingId(null)
                }}
                className={`flex items-center justify-center gap-3 p-4 rounded-xl font-medium transition-all duration-200 ${
                  activeTab === route.path
                    ? "bg-blue-600 text-white shadow-lg"
                    : "bg-white text-gray-700 border border-gray-200 hover:bg-blue-50 hover:border-blue-200"
                }`}
              >
                {route.icon}
                <span>{route.title}</span>
              </button>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Form Section */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center gap-3 mb-6">
              {activeRoute?.icon}
              <h2 className="text-xl font-semibold text-gray-900">
                {editingId ? "Update" : "Add"} {activeRoute?.title}
              </h2>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Name</label>
                <input
                  type="text"
                  placeholder={`Enter ${activeRoute?.title.toLowerCase()} name`}
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                  required
                />
              </div>
              <button
                type="submit"
                className="w-full bg-blue-600 hover:bg-blue-700 text-white py-3 px-4 rounded-lg font-medium transition-colors flex items-center justify-center gap-2"
              >
                {editingId ? (
                  <>
                    <Edit className="w-4 h-4" />
                    Update {activeRoute?.title}
                  </>
                ) : (
                  <>
                    <Plus className="w-4 h-4" />
                    Add {activeRoute?.title}
                  </>
                )}
              </button>
              {editingId && (
                <button
                  type="button"
                  onClick={() => {
                    setInput("")
                    setEditingId(null)
                  }}
                  className="w-full bg-gray-100 hover:bg-gray-200 text-gray-700 py-3 px-4 rounded-lg font-medium transition-colors"
                >
                  Cancel
                </button>
              )}
            </form>
          </div>

          {/* List Section */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center gap-3">
                {activeRoute?.icon}
                <h2 className="text-xl font-semibold text-gray-900">{activeRoute?.title} List</h2>
                <span className="bg-blue-100 text-blue-800 text-sm font-medium px-2.5 py-0.5 rounded-full">
                  {data.length}
                </span>
              </div>
            </div>

            <div className="max-h-96 overflow-y-auto">
              {data.length > 0 ? (
                <table className="w-full">
                  <thead className="bg-gray-50 sticky top-0">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Name
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {data.map((item, index) => (
                      <tr key={item._id} className="hover:bg-gray-50 transition-colors">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center mr-3">
                              <span className="text-blue-600 font-medium text-sm">{index + 1}</span>
                            </div>
                            <span className="text-sm font-medium text-gray-900">{item.stageName || item.sectorName || item.industryName}</span>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right">
                          <div className="flex items-center justify-end gap-2">
                            <button
                              onClick={() => handleEdit(item)}
                              className="inline-flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-blue-600 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors"
                            >
                              <Edit className="w-3 h-3" />
                              Edit
                            </button>
                            <button
                              onClick={() => handleDeleteClick(item._id)}
                              className="inline-flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-red-600 bg-red-50 rounded-lg hover:bg-red-100 transition-colors"
                            >
                              <Trash2 className="w-3 h-3" />
                              Delete
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <div className="p-12 text-center">
                  <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    {activeRoute?.icon && React.cloneElement(activeRoute.icon, { className: "w-8 h-8 text-gray-400" })}
                  </div>
                  <h3 className="text-lg font-medium text-gray-900 mb-2">No {activeRoute?.title} Found</h3>
                  <p className="text-gray-500">Get started by adding your first {activeRoute?.title.toLowerCase()}.</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
            <div className="flex items-center gap-4 mb-4">
              <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                <Trash2 className="w-6 h-6 text-red-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Confirm Delete</h3>
                <p className="text-sm text-gray-500">This action cannot be undone</p>
              </div>
            </div>

            <p className="text-gray-700 mb-6">
              Are you sure you want to delete this {activeRoute?.title.toLowerCase()}? This will permanently remove it
              from the system.
            </p>

            <div className="flex gap-3">
              <button
                onClick={() => setShowConfirm(false)}
                className="flex-1 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg font-medium transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                className="flex-1 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg font-medium transition-colors"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}